import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useQuery } from '@tanstack/vue-query'

import { createQueryKey, isContentType } from '@/utils'
import { useAppRouting } from '@/composables/useAppRouting'
import { useContentApi } from '@/composables/useCmsContentApi'
import { useCmsContentSearch } from '@/composables/useCmsContentSearch'
import { mapCmsTask } from '@/utils/cmsContentMappings'
import IbexaContentTypeIdentifier from '@/enums/IbexaContentTypeIdentifier'
import RouteParam from '@/enums/RouteParam'
import type { AnchorNavigationPage } from '@/types/AnchorNavigationPage'
import type { AssignmentSet, TaskFrontpage } from '@/types/Task'
import type { Folder } from '@/types/Folder'
import type { IFlytTask } from '@/types/Flyt'
import type { StepTask } from '@/types/Task'
import type { SubjectResource } from '@/types/Subject'
import type { TextImage } from '@/types/TextImage'

export const useSubjectResource = (contentId?: number) => {
  const isAssignmentCollection = ref<boolean | null>(null)
  const assignmentSets = ref<AssignmentSet[]>([])
  const stepTasks = ref<StepTask[]>([])
  const flytTasks = ref<IFlytTask[]>([])
  const taskFrontpage = ref<TaskFrontpage | null>(null)
  const resourceGuide = ref<AnchorNavigationPage | null>(null)
  const introductionStepId = ref<string | null>(null)

  const { search } = useCmsContentSearch()
  const { getContentIdFromRouteParam } = useAppRouting()
  const {
    getAllChildrenElements,
    getCurrentElement,
    getSubjectResourceChildren
  } = useContentApi()

  const subjectResourceContentId = contentId ?? getContentIdFromRouteParam(RouteParam.SubjectResource) as number
  const subjectResourceQuery = useQuery<SubjectResource>({
    queryKey: createQueryKey('getCurrentElement', subjectResourceContentId),
    queryFn: () => getCurrentElement(subjectResourceContentId),
    enabled: computed(() => !!subjectResourceContentId),
    staleTime: Infinity,
  })

  onMounted(async () => {
    const subjectResourceChildren = await getSubjectResourceChildren(subjectResourceContentId)
    const folders: Folder[] = subjectResourceChildren.filter(child => isContentType(child, IbexaContentTypeIdentifier.vgsFolder)) as Folder[]
    const assignments = await Promise.all(
      folders.map(async (folder: Folder) => {
        const folderChildren = await getAllChildrenElements(folder.contentId)
        const filteredFolderChildren: SubjectResource[] = folderChildren.filter(child => isContentType(child, IbexaContentTypeIdentifier.vgsSubjectResource)) as SubjectResource[]

        const assignmentContentIds = filteredFolderChildren.map(assignment => assignment.contentId)

        return {
          image: folder.image,
          title: folder.title,
          folderContentId: folder.contentId,
          assignmentContentIds: assignmentContentIds,
        }
      })
    )
    assignmentSets.value = assignments
    stepTasks.value = subjectResourceChildren.filter(child => isContentType(child, IbexaContentTypeIdentifier.vgsStepTask)) as StepTask[]
    flytTasks.value = subjectResourceChildren.filter(child => isContentType(child, IbexaContentTypeIdentifier.vgsFlytTask)) as IFlytTask[]
    taskFrontpage.value = subjectResourceChildren.find(child => isContentType(child, IbexaContentTypeIdentifier.vgsTaskFrontpage)) as TaskFrontpage
    resourceGuide.value = subjectResourceChildren.find(child => isContentType(child, IbexaContentTypeIdentifier.vgsResourceGuide)) as AnchorNavigationPage
    isAssignmentCollection.value = assignmentSets.value.length > 0
    // Temporary backwards compatibility, can be deleted after VGS-473
    if (taskFrontpage.value) return

    taskFrontpage.value = subjectResourceChildren.find(child => child.contentTypeIdentifier === IbexaContentTypeIdentifier.vgsTextImageElement) as TextImage
  })

  const isAnchorNavigationTask = computed(() => subjectResource.value?.isAnchorNavigationTask)
  const subjectResource = computed(() => subjectResourceQuery.data.value)
  const subjectResourceTitle = computed(() => subjectResourceQuery.data.value?.title ?? '')
  const subjectResourceCategory = computed(() => subjectResourceQuery.data.value?.category ?? '')
  const subjectResourceIntroduction = computed(() => subjectResourceQuery.data.value?.introduction ?? '')

  watch(subjectResource, () => introductionStepId.value = subjectResource.value?.introductionStepId ?? null)

  const createIntroductionStepSearchQuery = () => ({
    ['contentTypes[]']: IbexaContentTypeIdentifier.vgsStepTask,
    ['contentId[]']: introductionStepId.value as string,
    limit: '1',
  })
  const { data: introductionStepData } = useQuery({
    queryKey: ['introductionStep', subjectResource],
    queryFn: () => search(createIntroductionStepSearchQuery()),
    enabled: computed(() => !!introductionStepId.value),
    staleTime: Infinity,
  })

  const introductionStep = computed(() => {
    if (!introductionStepData.value?.[0]) return

    return mapCmsTask(introductionStepData.value[0])
  })

  return {
    assignmentSets,
    isAssignmentCollection,
    isAnchorNavigationTask,
    isLoading: subjectResourceQuery.isLoading,
    flytTasks,
    resourceGuide,
    subjectResource,
    subjectResourceCategory,
    subjectResourceTitle,
    subjectResourceIntroduction,
    stepTasks,
    taskFrontpage,
    introductionStep,
  }
}
